/**
 * WebAnalyticsPage — First-Party Website-Analytics pro Firma.
 * Besucher/Seitenaufrufe pro Tag, Top-Seiten, Quellen, Live-Ticker, Snippet.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { Globe, Copy, Radio, Users, Eye, Repeat, UserPlus, MousePointerClick, LogIn } from "lucide-react";
import PageHelp from "@/components/PageHelp";
import { buildTrackingSnippet } from "@/lib/snippets";
import TrackingLinksCard from "@/components/TrackingLinksCard";
import AppStoreStatsCard from "@/components/AppStoreStatsCard";
import WebsiteIntegrationCard from "@/components/WebsiteIntegrationCard";
import SiteChecklistCard from "@/components/SiteChecklistCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SOURCE_LABEL = {
  google_ads: "Google Ads", meta_ads: "Meta Ads", search_organic: "Suche (organisch)",
  social: "Social Media", referral: "Verweis", direct: "Direkt", campaign: "Kampagne (UTM)",
};
const SOURCE_COLOR = {
  google_ads: "bg-blue-100 text-blue-700 border-blue-200",
  meta_ads: "bg-indigo-100 text-indigo-700 border-indigo-200",
  search_organic: "bg-emerald-100 text-emerald-700 border-emerald-200",
  social: "bg-pink-100 text-pink-700 border-pink-200",
  referral: "bg-amber-100 text-amber-700 border-amber-200",
  campaign: "bg-violet-100 text-violet-700 border-violet-200",
  direct: "bg-slate-100 text-slate-600 border-slate-200",
};

const timeAgo = (iso) => {
  const s = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `vor ${s} Sek.`;
  if (s < 3600) return `vor ${Math.round(s / 60)} Min.`;
  return `vor ${Math.round(s / 3600)} Std.`;
};

function Kpi({ icon: Icon, label, value, testId }) {
  return (
    <Card>
      <CardContent className="pt-4 pb-4 flex items-center gap-3">
        <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
          <Icon className="w-4.5 h-4.5 w-5 h-5 text-[#2563EB]" />
        </div>
        <div>
          <div className="text-xl font-bold leading-tight" data-testid={testId}>{value}</div>
          <div className="text-[11px] text-slate-500">{label}</div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function WebAnalyticsPage() {
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState("");
  const [days, setDays] = useState("30");
  const [traffic, setTraffic] = useState("all");
  const [stats, setStats] = useState(null);
  const [live, setLive] = useState(null);
  const [showSnippet, setShowSnippet] = useState(false);

  useEffect(() => {
    axios.get(`${API}/companies`, { withCredentials: true })
      .then((r) => { setCompanies(r.data || []); if (r.data?.length) setCompanyId(r.data[0].id); })
      .catch(() => {});
  }, []);

  const load = useCallback(async (cid, d, tr) => {
    if (!cid) return;
    try {
      const r = await axios.get(`${API}/webtrack/${cid}/stats?days=${d}&traffic=${tr}`, { withCredentials: true });
      setStats(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Laden fehlgeschlagen");
    }
  }, []);

  const loadLive = useCallback(async (cid) => {
    if (!cid) return;
    try {
      const r = await axios.get(`${API}/webtrack/${cid}/live`, { withCredentials: true });
      setLive(r.data);
    } catch (e) { /* silent */ }
  }, []);

  useEffect(() => { load(companyId, days, traffic); }, [companyId, days, traffic, load]);
  useEffect(() => {
    loadLive(companyId);
    const iv = setInterval(() => loadLive(companyId), 10000);
    return () => clearInterval(iv);
  }, [companyId, loadLive]);

  const copySnippet = () => {
    navigator.clipboard.writeText(buildTrackingSnippet(companyId));
    toast.success("Tracking-Snippet kopiert");
  };

  const totals = stats?.totals || {};
  const hasData = (totals.pageviews || 0) > 0;

  return (
    <div className="p-6 space-y-5" data-testid="web-analytics-page">
      <PageHelp
        title="Website-Analytics"
        text="First-Party Besucher-Tracking direkt in InvictaFlow: Das Tracking-Snippet auf der Firmen-Website meldet jeden Seitenaufruf an die Plattform. Zusaetzlich erkennt es automatisch Formular-Absendungen (z.B. Probetraining, Offert-Anfrage) und legt daraus Leads an — inkl. Kampagnen-Zuordnung und CAPI-Sync. Kein Google Analytics noetig."
      />
      <div className="flex flex-wrap items-center gap-3">
        <Globe className="w-6 h-6 text-[#2563EB]" />
        <h1 className="text-2xl font-bold">Website-Analytics</h1>
        <div className="flex-1" />
        <Select value={traffic} onValueChange={setTraffic}>
          <SelectTrigger className="w-52" data-testid="webtrack-traffic-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle Besucher</SelectItem>
            <SelectItem value="organic">Nur Website (ohne Ads)</SelectItem>
            <SelectItem value="paid">Nur Ads-Traffic</SelectItem>
          </SelectContent>
        </Select>
        <Select value={days} onValueChange={setDays}>
          <SelectTrigger className="w-28" data-testid="webtrack-days-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="7">7 Tage</SelectItem>
            <SelectItem value="30">30 Tage</SelectItem>
            <SelectItem value="90">90 Tage</SelectItem>
          </SelectContent>
        </Select>
        <Select value={companyId} onValueChange={setCompanyId}>
          <SelectTrigger className="w-56" data-testid="webtrack-company-select"><SelectValue placeholder="Firma" /></SelectTrigger>
          <SelectContent>{companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <Card className="border-emerald-200">
        <CardContent className="pt-4 pb-4 flex flex-wrap items-center gap-3" data-testid="webtrack-live-ticker">
          <span className="relative flex h-2.5 w-2.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60" />
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" />
          </span>
          <Radio className="w-4 h-4 text-emerald-600" />
          <span className="text-sm font-semibold" data-testid="webtrack-online-count">
            {live ? `${live.online} Besucher gerade online` : "Live wird geladen..."}
          </span>
          {live?.recent?.[0] && (
            <span className="text-xs text-slate-500">
              Letzter Aufruf: <code className="bg-slate-100 px-1 rounded">{live.recent[0].path || "/"}</code> {timeAgo(live.recent[0].ts)}
            </span>
          )}
        </CardContent>
      </Card>

      {stats && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
            <Kpi icon={Users} label="Besucher" value={totals.visitors || 0} testId="webtrack-kpi-visitors" />
            <Kpi icon={MousePointerClick} label="Sessions" value={totals.sessions || 0} testId="webtrack-kpi-sessions" />
            <Kpi icon={Eye} label={`Seitenaufrufe (${totals.pages_per_session || 0}/Session)`} value={totals.pageviews || 0} testId="webtrack-kpi-pageviews" />
            <Kpi icon={UserPlus} label="Neue Besucher" value={totals.new_visitors || 0} testId="webtrack-kpi-new" />
            <Kpi icon={Repeat} label="Wiederkehrend" value={totals.returning_visitors || 0} testId="webtrack-kpi-returning" />
            <Kpi icon={UserPlus} label="Website-Leads" value={stats.web_leads || 0} testId="webtrack-kpi-leads" />
          </div>

          {stats.paid_split && (
            <Card>
              <CardContent className="pt-4 pb-4 flex flex-wrap items-center gap-x-8 gap-y-2 text-sm" data-testid="webtrack-paid-split">
                <span className="font-semibold text-xs text-slate-500 uppercase tracking-wide">Traffic-Trennung ({days} Tage)</span>
                <span className="flex items-center gap-2">
                  <Badge className="bg-blue-100 text-blue-700 border-blue-200 text-[10px]">Ads (Google/Meta)</Badge>
                  <b>{stats.paid_split.paid.visitors}</b> Besucher
                  <span className="text-xs text-slate-400">({stats.paid_split.paid.views} Aufrufe)</span>
                </span>
                <span className="flex items-center gap-2">
                  <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-[10px]">Website organisch</Badge>
                  <b>{stats.paid_split.organic.visitors}</b> Besucher
                  <span className="text-xs text-slate-400">({stats.paid_split.organic.views} Aufrufe)</span>
                </span>
              </CardContent>
            </Card>
          )}

          {!hasData && (
            <Card className="border-amber-200 bg-amber-50/50">
              <CardContent className="pt-4 pb-4 text-sm text-amber-800" data-testid="webtrack-empty-hint">
                Noch keine Daten. Binde das Tracking-Snippet (unten) auf der Firmen-Website ein — ab dann erscheinen hier Besucher in Echtzeit.
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">Besucher & Seitenaufrufe pro Tag</CardTitle></CardHeader>
            <CardContent>
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={stats.daily} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="day" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                    <Tooltip />
                    <Bar dataKey="pageviews" name="Seitenaufrufe" fill="#93c5fd" radius={[3, 3, 0, 0]} />
                    <Bar dataKey="visitors" name="Besucher" fill="#2563EB" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          <div className="grid lg:grid-cols-3 gap-4">
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-base">Top-Seiten</CardTitle></CardHeader>
              <CardContent className="space-y-1.5" data-testid="webtrack-top-pages">
                {stats.top_pages.length === 0 && <p className="text-xs text-slate-400">Noch keine Seitenaufrufe.</p>}
                {stats.top_pages.map((p) => (
                  <div key={p.path} className="flex items-center gap-2 text-sm border-b border-slate-100 pb-1.5 last:border-0">
                    <code className="text-xs bg-slate-50 px-1.5 py-0.5 rounded flex-1 truncate">{p.path || "/"}</code>
                    <span className="text-xs text-slate-500">{p.visitors} Bes.</span>
                    <Badge variant="outline" className="text-[10px]">{p.views} Aufrufe</Badge>
                  </div>
                ))}
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-1.5"><LogIn className="w-4 h-4" /> Einstiegsseiten</CardTitle></CardHeader>
              <CardContent className="space-y-1.5" data-testid="webtrack-entry-pages">
                {(!stats.entry_pages || stats.entry_pages.length === 0) && <p className="text-xs text-slate-400">Noch keine Sessions.</p>}
                {stats.entry_pages?.map((p) => (
                  <div key={p.path} className="flex items-center gap-2 text-sm border-b border-slate-100 pb-1.5 last:border-0">
                    <code className="text-xs bg-slate-50 px-1.5 py-0.5 rounded flex-1 truncate">{p.path || "/"}</code>
                    <Badge variant="outline" className="text-[10px]">{p.sessions} Sessions</Badge>
                  </div>
                ))}
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-base">Herkunft der Besucher</CardTitle></CardHeader>
              <CardContent className="space-y-1.5" data-testid="webtrack-sources">
                {stats.sources.length === 0 && <p className="text-xs text-slate-400">Noch keine Daten.</p>}
                {stats.sources.map((s) => (
                  <div key={s.source} className="flex items-center gap-2 text-sm border-b border-slate-100 pb-1.5 last:border-0">
                    <Badge className={`text-[10px] ${SOURCE_COLOR[s.source] || SOURCE_COLOR.direct}`}>
                      {SOURCE_LABEL[s.source] || s.source}
                    </Badge>
                    <div className="flex-1" />
                    <span className="text-xs text-slate-500">{s.visitors} Besucher</span>
                    <span className="text-xs text-slate-400">({s.views} Aufrufe)</span>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </>
      )}

      {companyId && <SiteChecklistCard companyId={companyId} />}

      {companyId && <WebsiteIntegrationCard companyId={companyId} />}

      {companyId && <TrackingLinksCard companyId={companyId} />}

      {companyId && <AppStoreStatsCard companyId={companyId} />}

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            Tracking-Snippet
            <div className="flex-1" />
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setShowSnippet((s) => !s)} data-testid="webtrack-toggle-snippet">
              {showSnippet ? "Ausblenden" : "Anzeigen"}
            </Button>
            <Button size="sm" className="h-7 text-xs bg-[#2563EB] hover:bg-[#1d4ed8]" onClick={copySnippet} data-testid="webtrack-copy-snippet">
              <Copy className="w-3 h-3 mr-1" /> Kopieren
            </Button>
          </CardTitle>
          <p className="text-xs text-slate-500">
            Alternative für Spezialfälle: manuelles Basis-Snippet <b>ohne</b> Werbe-Pixel und Cookie-Banner. Wer das zentrale Lade-Script oben nutzt, braucht dieses Snippet <b>nicht</b>.
          </p>
        </CardHeader>
        {showSnippet && companyId && (
          <CardContent>
            <pre className="text-[10px] bg-slate-50 border border-[var(--studio-border)] rounded-md p-2.5 overflow-x-auto whitespace-pre-wrap break-all max-h-64">{buildTrackingSnippet(companyId)}</pre>
          </CardContent>
        )}
      </Card>
    </div>
  );
}
